import React, { useEffect, useRef, useState } from 'react';
import { IconCode, IconChartLine, IconCopy, IconCheck } from '@tabler/icons-react';
import { Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import mermaid from 'mermaid';

import classes from './mermaid.module.css';

// 初始化 Mermaid 配置
let isInitialized = false;

const initMermaid = () => {
  if (!isInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'base',
      securityLevel: 'loose',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      // 启用所有图表类型
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis',
      },
      sequence: { useMaxWidth: true },
      gantt: { useMaxWidth: true },
      pie: { useMaxWidth: true },
      // 禁用严格模式，允许更宽松的语法
      deterministicIds: false,
      // 设置日志级别
      logLevel: 'fatal',
      // 自定义主题变量 - 浅色主题
      themeVariables: {
        // 主要颜色 - 柔和的蓝紫色
        primaryColor: '#e0e7ff',
        primaryTextColor: '#1e293b',
        primaryBorderColor: '#6366f1',

        // 线条颜色
        lineColor: '#6366f1',

        // 次要和第三色
        secondaryColor: '#fef3c7',
        tertiaryColor: '#dbeafe',

        // 背景色 - 确保是白色或浅色
        background: '#ffffff',
        mainBkg: '#e0e7ff',
        secondBkg: '#fef3c7',
        tertiaryBkg: '#dbeafe',

        // 文本颜色
        textColor: '#1e293b',

        // 边框颜色
        border1: '#6366f1',
        border2: '#f59e0b',

        // 注释颜色
        note: '#fef3c7',
        noteBorder: '#f59e0b',
        noteBkgColor: '#fef3c7',
        noteTextColor: '#78350f',

        // 字体大小
        fontSize: '16px',

        // 节点颜色
        nodeBorder: '#6366f1',
        clusterBkg: '#f3f4f6',
        clusterBorder: '#d1d5db',

        // 默认链接样式
        defaultLinkColor: '#6366f1',

        // 边缘标签背景
        edgeLabelBackground: '#ffffff',

        // 其他颜色
        nodeTextColor: '#1e293b',
      },
    });
    isInitialized = true;
  }
};

interface MermaidBlockProps {
  chart: string;
}

export const MermaidBlock: React.FC<MermaidBlockProps> = ({ chart }) => {
  const { t } = useTranslation();
  const elementRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSource, setShowSource] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const renderTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 清除之前的定时器
    if (renderTimerRef.current) {
      clearTimeout(renderTimerRef.current);
    }

    const renderChart = async () => {
      if (!chart) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        // 确保 Mermaid 已初始化
        initMermaid();

        // 清理图表内容：移除首尾空白，但保留内部换行
        const cleanedChart = chart.trim();

        if (!cleanedChart) {
          setError(t('mermaid.error.empty_content'));
          setIsLoading(false);
          return;
        }

        // 简单验证：检查是否是完整的 Mermaid 代码
        // 1. 必须有图表类型关键字（支持所有 Mermaid v11 官方图表类型）
        const hasValidStart =
          /^(%%\{|graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|stateDiagram-v2|erDiagram|entityRelationshipDiagram|journey|userJourney|gantt|pie|quadrantChart|requirementDiagram|gitGraph|gitgraph|c4Context|c4Container|c4Component|c4Dynamic|c4Deployment|mindmap|timeline|zenuml|sankey|xychart-beta|xychart|block-beta|block|packet-beta|packet|kanban|architecture|radar|treemap)/i.test(
            cleanedChart
          );

        if (!hasValidStart) {
          // 不是有效的 Mermaid 代码，暂时不渲染（可能正在流式输出）
          setIsLoading(false);
          setSvg('');
          return;
        }

        // 2. 检查是否有未闭合的语句（流式输出中可能出现）
        // 如果最后一行是不完整的箭头或连接符，跳过渲染
        const lines = cleanedChart.split('\n');
        const lastLine = lines[lines.length - 1]?.trim();
        if (lastLine && /(-+>|--|\||=+>|\.+>)$/.test(lastLine)) {
          // 最后一行以箭头或连接符结尾，说明可能不完整
          setIsLoading(false);
          return;
        }

        // 生成唯一 ID
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // 渲染图表
        const { svg: renderedSvg } = await mermaid.render(id, cleanedChart);

        // 设置渲染后的 SVG
        setSvg(renderedSvg);
      } catch (err) {
        // 对于明确的错误（不是流式输出导致的），显示错误信息
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        // 由于渲染失败，需要清理临时元素
        document.querySelectorAll(`[id^="dmermaid-"]`).forEach((el) => {
          if (el.parentNode) {
            // 删除包含错误信息的元素（.error-text 可能在 SVG 内部）
            const hasErrorText = el.querySelector('.error-text') || el.querySelector('text.error-text');
            if (hasErrorText) {
              el.parentNode.removeChild(el);
            }
            // 删除空容器或不包含有效 SVG 的元素
            else if (!el.querySelector('svg')) {
              el.parentNode.removeChild(el);
            }
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    // 使用防抖，延迟 300ms 后再渲染
    // 这样可以避免流式输出过程中频繁触发渲染
    renderTimerRef.current = setTimeout(() => {
      renderChart();
    }, 300);

    // 清理函数
    return () => {
      if (renderTimerRef.current) {
        clearTimeout(renderTimerRef.current);
      }
    };
  }, [chart]);

  // 复制功能
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(chart);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // 切换源码/图表
  const toggleView = () => {
    setShowSource(!showSource);
  };

  if (isLoading) {
    return (
      <div className={`${classes.mermaidContainer} flex justify-center items-center`}>
        <Loader color="violet" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${classes.mermaidContainer}`}>
        <p className="text-red-800 text-sm font-medium">{t('mermaid.error.title')}</p>
        <p className="text-red-600 text-xs mt-2 whitespace-pre-wrap break-words overflow-hidden font-sans">{error}</p>
        <details className="mt-2">
          <summary className="text-gray-600 text-xs cursor-pointer hover:text-gray-800">
            {t('mermaid.error.view_source')}
          </summary>
          <pre className="text-gray-700 text-xs mt-2 p-2 bg-gray-100 rounded overflow-x-auto whitespace-pre-wrap">
            {chart}
          </pre>
        </details>
      </div>
    );
  }

  // 如果没有渲染的 SVG，暂不显示（避免流式输出过程中闪烁）
  if (!svg) {
    return null;
  }

  return (
    <div className="relative">
      {/* 右上角工具栏 */}
      <div className="absolute top-2 right-2 z-10 flex space-x-1">
        {/* 切换源码/图表按钮 */}
        <button
          onClick={toggleView}
          className="p-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
          title={showSource ? t('mermaid.view_chart') : t('mermaid.view_source')}
        >
          {showSource ? <IconChartLine size={16} /> : <IconCode size={16} />}
        </button>

        {/* 复制按钮 */}
        {/* <button
          onClick={handleCopy}
          className="p-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
          title="复制源码"
        >
          {copied ? (
            <IconCheck size={16} className="text-green-600" />
          ) : (
            <IconCopy size={16} className="text-gray-700" />
          )}
        </button> */}
      </div>

      {/* 内容区域 */}
      {showSource ? (
        // 显示源码
        <div className="bg-gray-800 rounded-md overflow-hidden p-4">
          <pre className="overflow-x-auto text-sm m-0">
            <code className="text-gray-100 font-mono">{chart}</code>
          </pre>
        </div>
      ) : (
        // 显示图表
        <div className={classes.mermaidContainer}>
          <div ref={elementRef} className={classes.mermaidDiagram} dangerouslySetInnerHTML={{ __html: svg }} />
        </div>
      )}
    </div>
  );
};
